import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MagicSearchControllerService } from '@iota/inh-core-v1-management-client';
import { catchError, distinctUntilChanged, map, Observable, of, startWith, switchMap, timer } from 'rxjs';
import { OverlayService } from '../../shared/services/overlay.service';
import { MagicSearchOverlayComponent } from '../components/magic-search-overlay/magic-search-overlay.component';
import { IndexedMagicSearchGroup, SearchState } from '../models/magic-search.model';
import { toGroups } from './magic-search.mapper';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 3;

@Injectable({
    providedIn: 'root'
})
export class MagicSearchService {
    private readonly api = inject(MagicSearchControllerService);
    private readonly router = inject(Router);
    private readonly overlay = inject(OverlayService);

    readonly query = signal('');
    readonly selectedIndex = signal(0);

    private readonly response = toSignal(
        toObservable(this.query).pipe(
            map((value) => value.trim()),
            distinctUntilChanged(),
            switchMap((value): Observable<SearchState> => {
                if (value.length < MIN_CHARS) {
                    return of({ status: 'idle' });
                }
                return timer(DEBOUNCE_MS).pipe(
                    switchMap(() =>
                        this.api.search(value).pipe(
                            map((res): SearchState => ({ status: 'ok', res })),
                            catchError((): Observable<SearchState> => of({ status: 'error' })),
                            startWith<SearchState>({ status: 'loading' })
                        )
                    )
                );
            })
        ),
        { initialValue: { status: 'idle' } as SearchState }
    );

    readonly loading = computed(() => this.response().status === 'loading');
    readonly error = computed(() => this.response().status === 'error');
    private readonly groups = computed(() => {
        const state = this.response();
        return state.status === 'ok' ? toGroups(state.res) : [];
    });
    readonly hasResults = computed(() => this.groups().length > 0);
    readonly empty = computed(() => this.response().status === 'ok' && this.groups().length === 0);
    readonly idle = computed(() => this.response().status === 'idle');
    readonly indexedGroups = computed<IndexedMagicSearchGroup[]>(() => {
        let index = 0;
        return this.groups().map((group) => ({
            type: group.type,
            labelId: group.labelId,
            rows: group.rows.map((row) => ({ ...row, index: index++ }))
        }));
    });
    private readonly flatRows = computed(() => this.indexedGroups().flatMap((group) => group.rows));

    private wasOpen = false;

    constructor() {
        effect(() => {
            const open = this.overlay.isOpen();
            if (this.wasOpen && !open) {
                this.query.set('');
                this.selectedIndex.set(0);
            }
            this.wasOpen = open;
        });
    }

    open(): void {
        if (this.overlay.isOpen()) {
            return;
        }
        this.query.set('');
        this.selectedIndex.set(0);
        this.overlay.open({ component: MagicSearchOverlayComponent });
    }

    close(): void {
        this.overlay.close();
    }

    setQuery(value: string): void {
        this.query.set(value);
        this.selectedIndex.set(0);
    }

    moveSelection(delta: number): void {
        const lastIndex = this.flatRows().length - 1;
        if (lastIndex < 0) {
            return;
        }
        const next = Math.min(Math.max(this.selectedIndex() + delta, 0), lastIndex);
        this.selectedIndex.set(next);
    }

    navigateSelected(): void {
        const row = this.flatRows()[this.selectedIndex()];
        if (row?.link) {
            this.router.navigateByUrl(row.link);
            this.close();
        }
    }
}
